import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { useToken } from '@app/hooks';
import { useCopyPortfolioStore } from '@app/hooks/copyTrading/copyPortfolioStore';
import { useMyPortfolio } from '@app/hooks/copyTrading/useMyPortfolio';
import { useMyProfile } from '@app/hooks/user/myprofile';
import { useTradingWallets } from '@app/hooks/user/useTradingWallets';
import TransferModal from '../QBIT/Assets/components/TransferModal';
import { CopyRegisterModal } from '../CopyTradePage/modal/CopyRegisterModal';
import { FollowPortfolioModal } from '../CopyTradePage/modal/FollowPortfolioModal';
import { EditFollowModal } from '../CopyTradePage/modal/EditFollowModal';
import { getMyCopyOrders, getPortfolios } from '../../api/copyTrading';
import type {
  CopyPortfolio,
  CopyOrder,
  CopyFollow,
} from '../../../types/copyTrading';
import BigNumber from 'bignumber.js';
import dayjs from 'dayjs';
import DefaultProfileImage from './assets/profile.png';
import CSelect, {
  SelectDropdownGlobalStyle,
} from '@app/components/Common/CSelect';

// 심볼 필터 옵션 (DB exchange_pairs 기준)
const getSymbolOptions = (t: (key: string) => string) => [
  { value: 'all', label: t('allSymbols') },
  { value: 'BTC', label: 'BTC/USDT' },
  { value: 'ETH', label: 'ETH/USDT' },
  { value: 'XRP', label: 'XRP/USDT' },
  { value: 'BCH', label: 'BCH/USDT' },
  { value: 'LTC', label: 'LTC/USDT' },
  { value: 'EOS', label: 'EOS/USDT' },
  { value: 'XMR', label: 'XMR/USDT' },
  { value: 'ADA', label: 'ADA/USDT' },
  { value: 'AVAX', label: 'AVAX/USDT' },
  { value: 'DOGE', label: 'DOGE/USDT' },
  { value: 'DOT', label: 'DOT/USDT' },
  { value: 'UNI', label: 'UNI/USDT' },
  { value: 'LINK', label: 'LINK/USDT' },
  { value: 'SOL', label: 'SOL/USDT' },
  { value: 'TRX', label: 'TRX/USDT' },
  { value: '1000PEPE', label: '1000PEPE/USDT' },
  { value: 'SUI', label: 'SUI/USDT' },
];

// 상태 필터 옵션 (번역 키 사용)
const getStatusOptions = (t: (key: string) => string) => [
  { value: 'all', label: t('allStatus') },
  { value: '0', label: t('pending') },
  { value: '1', label: t('filled') },
  { value: '2', label: t('cancelled') },
];

// New Section Components
import {
  MyPortfolioSection,
  FollowersSection,
  EarningsSection,
} from './sections';

// Tab Type - Extended with leader-only tabs
type TabType =
  | 'discover'
  | 'following'
  | 'orders'
  | 'my-portfolio'
  | 'followers'
  | 'earnings';

const MobileCopyTradingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useToken();
  const { hasPortfolio, portfolio: myPortfolioData } = useMyPortfolio();
  const { data: myProfile } = useMyProfile();

  const [activeTab, setActiveTab] = useState<TabType>('discover');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [openTransferModal, setOpenTransferModal] = useState(false);
  const [openRegisterModal, setOpenRegisterModal] = useState(false);
  const [openFollowModal, setOpenFollowModal] = useState(false);
  const [openEditFollowModal, setOpenEditFollowModal] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] =
    useState<CopyPortfolio | null>(null);
  const [selectedFollow, setSelectedFollow] = useState<CopyFollow | null>(null);
  const [selectedFollowPortfolio, setSelectedFollowPortfolio] =
    useState<CopyPortfolio | null>(null);
  const [portfoliosPage, setPortfoliosPage] = useState(1);
  const [totalPortfolios, setTotalPortfolios] = useState(0);

  // Order History state
  const [orders, setOrders] = useState<CopyOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [totalOrders, setTotalOrders] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  const {
    portfolios,
    myFollows,
    fetchPortfolios,
    fetchMyFollows,
    isLoading,
    summary,
  } = useCopyPortfolioStore();

  // 이미지 URL 가져오기
  const getPortfolioImageUrl = (portfolio: CopyPortfolio): string | null => {
    const imageUrl =
      portfolio.imageUrl ||
      portfolio.metadata?.imageUrl ||
      (portfolio as any).leaderProfileImage ||
      (portfolio as any).leaderAvatar;
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${import.meta.env.VITE_APP_API_MEDIA_DEV_URL}/${imageUrl}`;
  };

  // 기본 아바타 이미지
  const DEFAULT_AVATAR = DefaultProfileImage;

  const { data: walletData } = useTradingWallets({
    enabled: activeTab === 'following' && !!token,
  });

  const fetchOrders = useCallback(async () => {
    if (!token) return;

    setOrdersLoading(true);
    const params = {
      page: ordersPage,
      limit: 20,
      symbol: selectedSymbol === 'all' ? undefined : selectedSymbol,
      status: selectedStatus === 'all' ? undefined : selectedStatus,
    };
    console.log('[MobileCopyTradingPage] fetchOrders params:', params);

    try {
      const response = await getMyCopyOrders(params);
      console.log('[MobileCopyTradingPage] fetchOrders response:', response);

      if (response.success && response.data) {
        setOrders(response.data.items || []);
        setTotalOrders(response.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('[MobileCopyTradingPage] fetchOrders error:', error);
    } finally {
      setOrdersLoading(false);
    }
  }, [token, ordersPage, selectedSymbol, selectedStatus]);

  useEffect(() => {
    if (activeTab === 'discover') {
      const loadPortfolios = async () => {
        // ⭐ period='7d'로 요청하여 7일간 ROI/PnL 데이터 가져오기
        await fetchPortfolios({
          status: 1,
          page: portfoliosPage,
          pageSize: 20,
          period: '7d',
        });
        // API를 직접 호출하여 total 가져오기
        try {
          const response = await getPortfolios({
            status: 1,
            page: portfoliosPage,
            pageSize: 20,
            period: '7d',
          });
          if (response?.success && response.data) {
            setTotalPortfolios(response.data.total || 0);
          }
        } catch (error) {
          console.error(
            '[MobileCopyTradingPage] Failed to get total portfolios:',
            error
          );
        }
      };
      loadPortfolios();
    } else if (activeTab === 'following' && token) {
      fetchMyFollows();
    } else if (activeTab === 'orders' && token) {
      fetchOrders();
    }
  }, [
    activeTab,
    token,
    portfoliosPage,
    fetchPortfolios,
    fetchMyFollows,
    fetchOrders,
  ]);

  const filteredPortfolios = useMemo(() => {
    if (!portfolios) return [];
    return portfolios.filter((portfolio) => {
      const keyword = searchKeyword.toLowerCase();
      return (
        portfolio.leaderUsername?.toLowerCase().includes(keyword) ||
        portfolio.portfolioName?.toLowerCase().includes(keyword)
      );
    });
  }, [portfolios, searchKeyword]);

  const isFollowing = (portfolioId: number) => {
    // 종료된 팔로우(status 3)는 제외하고 활성 팔로우만 체크
    return (myFollows || []).some(
      (f) => f.follow.portfolioId === portfolioId && f.follow.status !== 3
    );
  };

  const isMyPortfolio = (portfolio: CopyPortfolio) => {
    // myPortfolioData의 id와 비교하여 자신의 포트폴리오인지 확인
    if (myPortfolioData?.id && portfolio.id === myPortfolioData.id) {
      return true;
    }
    // fallback: leaderUserId 비교
    if (myProfile?.seq && portfolio.leaderUserId === myProfile.seq) {
      return true;
    }
    return false;
  };

  const generateChartData = (finalROI: number) => {
    const data: Array<{ value: number }> = [];
    const baseValue = 0;
    const finalValue = finalROI;

    for (let i = 0; i < 7; i++) {
      const progress = i / 6;
      const easedProgress =
        progress < 0.5
          ? 2 * progress * progress
          : -1 + (4 - 2 * progress) * progress;
      const value = baseValue + (finalValue - baseValue) * easedProgress;
      data.push({ value: Math.max(0, value) });
    }

    return data;
  };

  const handleCopyTrade = (portfolio: CopyPortfolio) => {
    setSelectedPortfolio(portfolio);
    setOpenFollowModal(true);
  };

  return (
    <Container>
      <TransferModal
        open={openTransferModal}
        onClose={() => setOpenTransferModal(false)}
        defaultFrom="spotwallet"
        defaultTo="copywallet"
      />
      <CopyRegisterModal
        open={openRegisterModal}
        setOpen={setOpenRegisterModal}
      />
      <FollowPortfolioModal
        open={openFollowModal}
        setOpen={setOpenFollowModal}
        portfolio={selectedPortfolio}
      />
      <EditFollowModal
        open={openEditFollowModal}
        setOpen={setOpenEditFollowModal}
        follow={selectedFollow}
        portfolio={selectedFollowPortfolio}
      />

      {/* Hero Section */}
      <HeroSection>
        <CharacterImage
          src="/assets/newHome/copy-trading/character.png"
          alt="Character"
        />
        <HeroContent>
          <HeroTitle>
            <TitleCopy>{t('copy')}</TitleCopy>
            <TitleTrading>{t('trading')}</TitleTrading>
          </HeroTitle>
          <HeroDescription>{t('copyTradingDescription')}</HeroDescription>
          <HeroButtons>
            <TransferButton onClick={() => setOpenTransferModal(true)}>
              {t('transfer')}
            </TransferButton>
            {!hasPortfolio && (
              <BecomeLeaderButton onClick={() => setOpenRegisterModal(true)}>
                {t('becomeALeader')}
              </BecomeLeaderButton>
            )}
          </HeroButtons>
        </HeroContent>
      </HeroSection>

      {/* Tabs Section */}
      <TabsSection>
        <TabsScrollContainer>
          <Tab
            $isActive={activeTab === 'discover'}
            onClick={() => setActiveTab('discover')}
          >
            {t('discover')}
          </Tab>
          <Tab
            $isActive={activeTab === 'following'}
            onClick={() => setActiveTab('following')}
          >
            {t('following')}
          </Tab>
          <Tab
            $isActive={activeTab === 'orders'}
            onClick={() => setActiveTab('orders')}
          >
            {t('orderHistory')}
          </Tab>
          {/* Leader-only tabs */}
          {hasPortfolio && (
            <>
              <TabDivider />
              <Tab
                $isActive={activeTab === 'my-portfolio'}
                onClick={() => setActiveTab('my-portfolio')}
                $isLeader
              >
                {t('myPortfolio')}
              </Tab>
              <Tab
                $isActive={activeTab === 'followers'}
                onClick={() => setActiveTab('followers')}
                $isLeader
              >
                {t('myFollowers')}
              </Tab>
            </>
          )}
          {/* Earnings tab - for all users (followers can see paid settlements) */}
          <Tab
            $isActive={activeTab === 'earnings'}
            onClick={() => setActiveTab('earnings')}
          >
            {t('earnings')}
          </Tab>
        </TabsScrollContainer>
      </TabsSection>

      {/* Content Section */}
      {activeTab === 'following' ? (
        <FollowingContentSection>
          <AllTradersSection>
            <AllTradersTitle>{t('allTraders')}</AllTradersTitle>
            <StatsCardMobile>
              <StatItemMobile>
                <StatLabelMobile>
                  {t('copyTradingWalletBalance')}
                </StatLabelMobile>
                <StatValueMobile>
                  {walletData?.copy
                    ? BigNumber(walletData.copy.balance || 0).toFixed(4)
                    : '0.0000'}
                  <StatUnitMobile>USDT</StatUnitMobile>
                </StatValueMobile>
              </StatItemMobile>
              <StatItemMobile>
                <StatLabelMobile>{t('totalAllocated')}</StatLabelMobile>
                <StatValueMobile>
                  {summary?.totalAllocated
                    ? BigNumber(summary.totalAllocated).toFixed(4)
                    : '0.0000'}
                  <StatUnitMobile>USDT</StatUnitMobile>
                </StatValueMobile>
              </StatItemMobile>
              <StatItemMobile>
                <StatLabelMobile>{t('totalPnl')}</StatLabelMobile>
                <StatValueMobile $isPositive={true}>
                  {summary?.totalPnl
                    ? `${summary.totalPnl >= 0 ? '+' : ''}${BigNumber(summary.totalPnl).toFixed(2)}`
                    : '+0.00'}
                  <StatUnitMobile>USDT</StatUnitMobile>
                </StatValueMobile>
              </StatItemMobile>
              <StatItemMobile>
                <StatLabelMobile>{t('profitShared')}</StatLabelMobile>
                <StatValueMobile>
                  {summary?.totalProfitShared
                    ? BigNumber(summary.totalProfitShared).toFixed(4)
                    : '0.0000'}
                  <StatUnitMobile>USDT</StatUnitMobile>
                </StatValueMobile>
              </StatItemMobile>
            </StatsCardMobile>
          </AllTradersSection>

          <FollowingPortfoliosSection>
            <FollowingPortfoliosTitle>
              {t('followingPortfolios')}
            </FollowingPortfoliosTitle>
            {myFollows && myFollows.length > 0 ? (
              <PortfoliosList>
                {myFollows.map((item) => (
                  <FollowingPortfolioCard key={item.follow.id}>
                    <FollowingCardHeader>
                      <FollowingPortfolioName>
                        {item.portfolio?.portfolioName || t('unknown')}
                      </FollowingPortfolioName>
                      <FollowingStatusTag $status={item.follow.status}>
                        {item.follow.status === 1
                          ? t('active')
                          : item.follow.status === 2
                            ? t('paused')
                            : t('stopped')}
                      </FollowingStatusTag>
                    </FollowingCardHeader>
                    <FollowingCardStats>
                      <FollowingStatRow>
                        <FollowingStatLabel>
                          {t('copyRatio')}
                        </FollowingStatLabel>
                        <FollowingStatValue>
                          {item.follow.copyRatio
                            ? `${BigNumber(item.follow.copyRatio).multipliedBy(100).toFixed(0)}%`
                            : '0%'}
                        </FollowingStatValue>
                      </FollowingStatRow>
                      <FollowingStatRow>
                        <FollowingStatLabel>
                          {t('allocatedAmount')}
                        </FollowingStatLabel>
                        <FollowingStatValue>
                          {item.follow.allocatedAmount
                            ? BigNumber(item.follow.allocatedAmount).toFixed(2)
                            : '0.00'}{' '}
                          USDT
                        </FollowingStatValue>
                      </FollowingStatRow>
                      <FollowingStatRow>
                        <FollowingStatLabel>
                          {t('currentPnl')}
                        </FollowingStatLabel>
                        <FollowingStatValue
                          $isPositive={(item.follow.currentPnl || 0) >= 0}
                        >
                          {item.follow.currentPnl
                            ? `${item.follow.currentPnl >= 0 ? '+' : ''}${BigNumber(item.follow.currentPnl).toFixed(2)}`
                            : '+0.00'}{' '}
                          USDT
                        </FollowingStatValue>
                      </FollowingStatRow>
                      <FollowingStatRow>
                        <FollowingStatLabel>{t('roi')}</FollowingStatLabel>
                        <FollowingStatValue
                          $isPositive={(item.follow.roi || 0) >= 0}
                        >
                          {item.follow.roi
                            ? `${item.follow.roi >= 0 ? '+' : ''}${BigNumber(item.follow.roi).toFixed(2)}%`
                            : '0.00%'}
                        </FollowingStatValue>
                      </FollowingStatRow>
                    </FollowingCardStats>
                    <FollowingManageButton
                      onClick={() => {
                        setSelectedFollow(item.follow);
                        setSelectedFollowPortfolio(item.portfolio || null);
                        setOpenEditFollowModal(true);
                      }}
                    >
                      {t('manage')}
                    </FollowingManageButton>
                  </FollowingPortfolioCard>
                ))}
              </PortfoliosList>
            ) : (
              <EmptyStateContainer>
                <EmptyStateContent>
                  <SpeechBubbleMobile>
                    <SpeechBubbleImageMobile
                      src="/assets/newHome/copy-trading/speech-bubble-union.svg"
                      alt="Speech Bubble"
                    />
                    <SpeechBubbleTextMobile>
                      {t('startExploring')}
                    </SpeechBubbleTextMobile>
                  </SpeechBubbleMobile>
                  <CharacterImgMobile
                    src="/assets/newHome/copy-trading/empty-character.png"
                    alt="Character"
                  />
                </EmptyStateContent>
              </EmptyStateContainer>
            )}
          </FollowingPortfoliosSection>
        </FollowingContentSection>
      ) : activeTab === 'orders' ? (
        <OrderHistoryContentSection>
          <OrderHistoryHeader>
            <OrderHistoryTitle>{t('orderHistory')}</OrderHistoryTitle>
            <TotalOrdersCardMobile>
              <TotalOrdersLabelMobile>
                {t('totalOrders')}
              </TotalOrdersLabelMobile>
              <TotalOrdersValueMobile>
                <TotalOrdersNumberMobile>
                  {totalOrders.toFixed(4)}
                </TotalOrdersNumberMobile>
                <RefreshButtonMobile onClick={() => fetchOrders()}>
                  <RefreshIconMobile
                    src="/assets/newHome/copy-trading/redo-icon.svg"
                    alt="Refresh"
                  />
                  <RefreshTextMobile>{t('refresh')}</RefreshTextMobile>
                </RefreshButtonMobile>
              </TotalOrdersValueMobile>
            </TotalOrdersCardMobile>
          </OrderHistoryHeader>

          <FollowingPortfoliosSectionMobile>
            <FiltersBarMobile>
              <SelectDropdownGlobalStyle />
              <FiltersRowMobile>
                <CSelect
                  value={selectedSymbol}
                  onChange={(value) => {
                    setSelectedSymbol(value as string);
                    setOrdersPage(1);
                  }}
                  options={getSymbolOptions(t)}
                  style={{ width: 140, minWidth: 140 }}
                />
                <CSelect
                  value={selectedStatus}
                  onChange={(value) => {
                    setSelectedStatus(value as string);
                    setOrdersPage(1);
                  }}
                  options={getStatusOptions(t)}
                  style={{ width: 120, minWidth: 120 }}
                />
                <ReloadButtonMobile onClick={() => fetchOrders()}>
                  <ReloadIconMobile
                    src="/assets/newHome/copy-trading/reload-icon.svg"
                    alt="Reload"
                  />
                </ReloadButtonMobile>
              </FiltersRowMobile>
            </FiltersBarMobile>
            <OrdersTableContainerMobile>
              {ordersLoading ? (
                <EmptyTextMobile>{t('loading')}</EmptyTextMobile>
              ) : orders.length > 0 ? (
                <OrdersListMobile>
                  {orders.map((order) => (
                    <OrderItemMobile key={order.id}>
                      <OrderItemRow>
                        <OrderItemLabel>{t('time')}</OrderItemLabel>
                        <OrderItemValue>
                          {order.createdAt
                            ? dayjs(order.createdAt).format(
                                'YYYY-MM-DD HH:mm:ss'
                              )
                            : '-'}
                        </OrderItemValue>
                      </OrderItemRow>
                      <OrderItemRow>
                        <OrderItemLabel>{t('symbol')}</OrderItemLabel>
                        <OrderItemValue>{order.symbol || '-'}</OrderItemValue>
                      </OrderItemRow>
                      <OrderItemRow>
                        <OrderItemLabel>{t('side')}</OrderItemLabel>
                        <OrderItemValue>
                          {order.longShort === 'L'
                            ? t('long')
                            : order.longShort === 'S'
                              ? t('short')
                              : '-'}
                        </OrderItemValue>
                      </OrderItemRow>
                      <OrderItemRow>
                        <OrderItemLabel>{t('type')}</OrderItemLabel>
                        <OrderItemValue>
                          {order.limitMarket === 'L'
                            ? t('limit')
                            : order.limitMarket === 'M'
                              ? t('market')
                              : '-'}
                        </OrderItemValue>
                      </OrderItemRow>
                      <OrderItemRow>
                        <OrderItemLabel>{t('quantity')}</OrderItemLabel>
                        <OrderItemValue>
                          {order.quantity
                            ? BigNumber(order.quantity).toFixed(4)
                            : '-'}
                        </OrderItemValue>
                      </OrderItemRow>
                      <OrderItemRow>
                        <OrderItemLabel>{t('price')}</OrderItemLabel>
                        <OrderItemValue>
                          {order.price
                            ? BigNumber(order.price).toFixed(2)
                            : '-'}
                        </OrderItemValue>
                      </OrderItemRow>
                      <OrderItemRow>
                        <OrderItemLabel>{t('leverage')}</OrderItemLabel>
                        <OrderItemValue>
                          {order.leverage ? `${order.leverage}x` : '-'}
                        </OrderItemValue>
                      </OrderItemRow>
                      <OrderItemRow>
                        <OrderItemLabel>{t('status')}</OrderItemLabel>
                        <OrderItemValue>{order.status || '-'}</OrderItemValue>
                      </OrderItemRow>
                    </OrderItemMobile>
                  ))}
                </OrdersListMobile>
              ) : (
                <EmptyTextMobile>{t('noOrdersFound')}</EmptyTextMobile>
              )}
            </OrdersTableContainerMobile>
          </FollowingPortfoliosSectionMobile>
        </OrderHistoryContentSection>
      ) : activeTab === 'discover' ? (
        <ContentSection>
          <SectionTitle>{t('allTraders')}</SectionTitle>
          <SearchContainer>
            <SearchIcon
              src="/assets/newHome/copy-trading/search-icon.svg"
              alt="Search"
            />
            <SearchInput
              placeholder={t('searchTraders')}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
            />
          </SearchContainer>

          {isLoading ? (
            <LoadingText>{t('loading')}</LoadingText>
          ) : (
            <TradersList>
              {filteredPortfolios.map((portfolio, index) => {
                const following = isFollowing(portfolio.id);
                const myPortfolio = isMyPortfolio(portfolio);
                const rank = index + 1;

                return (
                  <TraderCard
                    key={portfolio.id}
                    $isMyPortfolio={myPortfolio}
                    $rank={rank}
                    onClick={() =>
                      navigate(`/copy-trade/portfolio/${portfolio.id}`)
                    }
                  >
                    {myPortfolio && (
                      <MyPortfolioBadge>{t('myPortfolio')}</MyPortfolioBadge>
                    )}
                    <CardHeader>
                      <TraderInfo>
                        <AvatarWrapper>
                          <AvatarImage
                            src={
                              getPortfolioImageUrl(portfolio) || DEFAULT_AVATAR
                            }
                            alt={portfolio.leaderUsername}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                DEFAULT_AVATAR;
                            }}
                          />
                          <RankBadge $rank={rank}>
                            <RankNumber>{rank}</RankNumber>
                          </RankBadge>
                        </AvatarWrapper>
                        <TraderDetails>
                          <TraderName>
                            {portfolio.leaderUsername || t('unknown')}
                          </TraderName>
                          <CopyTradesCount>
                            {portfolio.currentFollowers || 0} {t('copyTrades')}
                          </CopyTradesCount>
                        </TraderDetails>
                      </TraderInfo>
                      {rank <= 3 && (
                        <CrownIcon
                          src={`/assets/newHome/copy-trading/crown-rank${rank}.svg`}
                          alt={`Rank ${rank}`}
                        />
                      )}
                    </CardHeader>

                    <ROISection>
                      <ROILeft>
                        <ROILabel>{t('sevenDayRoi')}</ROILabel>
                        <ROIValue
                          $isNegative={
                            (portfolio.roi ?? portfolio.roi7d ?? 0) < 0
                          }
                        >
                          {/* ⭐ 7일 ROI 표시 */}
                          {(portfolio.roi !== undefined
                            ? portfolio.roi
                            : portfolio.roi7d) !== undefined
                            ? `${(portfolio.roi ?? portfolio.roi7d ?? 0) >= 0 ? '+' : ''}${(portfolio.roi ?? portfolio.roi7d ?? 0).toFixed(2)}%`
                            : '0.00%'}
                        </ROIValue>
                      </ROILeft>
                      <ChartWrapper>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={generateChartData(
                              portfolio.roi ?? portfolio.roi7d ?? 0
                            )}
                          >
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke={
                                (portfolio.roi ?? portfolio.roi7d ?? 0) >= 0
                                  ? '#07d993'
                                  : '#ef4444'
                              }
                              strokeWidth={2}
                              dot={false}
                              isAnimationActive={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </ChartWrapper>
                    </ROISection>

                    <CardMetrics>
                      <MetricItem>
                        <MetricLabel>{t('sevenDayPnl')}</MetricLabel>
                        <MetricValue>
                          {/* ⭐ 7일 PnL 표시 */}$
                          {(
                            portfolio.periodPnl !== undefined
                              ? portfolio.periodPnl
                              : portfolio.totalPnl
                          )
                            ? (
                                portfolio.periodPnl ?? portfolio.totalPnl
                              ).toFixed(2)
                            : '0.00'}
                        </MetricValue>
                      </MetricItem>
                      <MetricItem>
                        <MetricLabel>
                          {t('copyTradingPage.followers')}
                        </MetricLabel>
                        <MetricValue>
                          {portfolio.currentFollowers || 0}
                        </MetricValue>
                      </MetricItem>
                    </CardMetrics>

                    <CopyTradeButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyTrade(portfolio);
                      }}
                      disabled={following || myPortfolio}
                      $isFollowing={following}
                      $isMyPortfolio={myPortfolio}
                    >
                      {myPortfolio
                        ? t('myPortfolio')
                        : following
                          ? t('following')
                          : t('copyTrade')}
                    </CopyTradeButton>
                  </TraderCard>
                );
              })}
            </TradersList>
          )}

          {/* Pagination */}
          {(() => {
            const pageSize = 20;
            const totalPages = Math.ceil(totalPortfolios / pageSize);
            const shouldShowPagination =
              totalPages > 1 && filteredPortfolios.length > 0;

            if (!shouldShowPagination) return null;

            const maxVisiblePages = 5;
            let startPage = Math.max(
              1,
              portfoliosPage - Math.floor(maxVisiblePages / 2)
            );
            let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

            if (endPage - startPage < maxVisiblePages - 1) {
              startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }

            const pageNumbers: number[] = [];
            for (let i = startPage; i <= endPage; i++) {
              pageNumbers.push(i);
            }

            return (
              <PaginationContainer>
                <PaginationButton
                  onClick={() =>
                    setPortfoliosPage(Math.max(1, portfoliosPage - 1))
                  }
                  disabled={portfoliosPage === 1}
                >
                  <PaginationArrow
                    src="/assets/newHome/copy-trading/pagination-arrow-left.png"
                    alt="Prev"
                  />
                </PaginationButton>
                {pageNumbers.map((page) => (
                  <PaginationNumber
                    key={page}
                    $isActive={portfoliosPage === page}
                    onClick={() => setPortfoliosPage(page)}
                  >
                    {page}
                  </PaginationNumber>
                ))}
                <PaginationButton
                  onClick={() =>
                    setPortfoliosPage(Math.min(totalPages, portfoliosPage + 1))
                  }
                  disabled={portfoliosPage === totalPages}
                >
                  <PaginationArrow
                    src="/assets/newHome/copy-trading/pagination-arrow-right.png"
                    alt="Next"
                  />
                </PaginationButton>
              </PaginationContainer>
            );
          })()}
        </ContentSection>
      ) : activeTab === 'my-portfolio' ? (
        /* My Portfolio Section - Leader Dashboard */
        <MyPortfolioSection isMobile={true} />
      ) : activeTab === 'followers' ? (
        /* Followers Section - Leader's Followers Management */
        <FollowersSection isMobile={true} />
      ) : activeTab === 'earnings' ? (
        /* Earnings Section - Earnings & Settlements */
        <EarningsSection isMobile={true} />
      ) : null}
    </Container>
  );
};

export default MobileCopyTradingPage;

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #0d1117;
  padding-bottom: 100px;
`;

const HeroSection = styled.section`
  width: 100%;
  padding: 107px 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const CharacterImage = styled.img`
  width: 154px;
  height: 150px;
  object-fit: contain;
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 328px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const HeroTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Aggro', sans-serif;
  font-size: 29px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: -0.58px;
`;

const TitleCopy = styled.span`
  background: linear-gradient(90deg, #07d993 0%, #ffcc0d 50%, #ff8a00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const TitleTrading = styled.span`
  color: #ffffff;
`;

const HeroDescription = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #ffffff;
  line-height: 22px;
  letter-spacing: -0.32px;
  text-align: center;
`;

const HeroButtons = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
`;

const TransferButton = styled.button`
  flex: 1;
  padding: 16px 0;
  background: #07d993;
  border: none;
  border-radius: 12px;
  color: #0d1117;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.32px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #06c885;
  }
`;

const BecomeLeaderButton = styled.button`
  flex: 1;
  padding: 16px 0;
  background: transparent;
  border: 1px solid #07d993;
  border-radius: 12px;
  color: #07d993;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.32px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(7, 217, 147, 0.1);
  }
`;

const TabsSection = styled.section`
  width: 100%;
  padding: 0 16px;
  margin-top: 38px;
`;

const TabsScrollContainer = styled.div`
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const Tab = styled.button<{ $isActive: boolean; $isLeader?: boolean }>`
  padding: 11px 12px;
  background: ${(props) =>
    props.$isActive
      ? props.$isLeader
        ? 'rgba(255, 204, 13, 0.15)'
        : 'rgba(0, 110, 73, 0.08)'
      : 'transparent'};
  border: none;
  border-radius: 12px;
  color: ${(props) =>
    props.$isActive ? (props.$isLeader ? '#ffcc0d' : '#07d993') : '#ffffff'};
  font-family: 'NanumSquareRound', sans-serif;
  font-size: ${(props) => (props.$isActive ? '16px' : '14px')};
  font-weight: ${(props) => (props.$isActive ? '700' : '400')};
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
`;

const TabDivider = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.2);
  margin: auto 8px;
  flex-shrink: 0;
`;

const ContentSection = styled.section`
  width: 100%;
  padding: 0 16px;
  margin-top: 32px;
`;

const SectionTitle = styled.h2`
  font-family: 'Aggro', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;
  letter-spacing: -0.36px;
  margin-bottom: 16px;
`;

const SearchContainer = styled.div`
  width: 100%;
  height: 36px;
  background: rgba(0, 110, 73, 0.08);
  border: 1px solid rgba(7, 217, 147, 0.2);
  border-radius: 12px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const SearchIcon = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: #ffffff;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const LoadingText = styled.div`
  width: 100%;
  padding: 40px 0;
  text-align: center;
  color: #ffffff;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
`;

const TradersList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const getMobileRankCardStyles = (rank: number) => {
  switch (rank) {
    case 1:
      return {
        background: '#1f2520',
        border: '#ffd36a',
      };
    case 2:
      return {
        background: '#1b272f',
        border: '#e6e9ef',
      };
    case 3:
      return {
        background: '#1f2428',
        border: '#e0a370',
      };
    default:
      return null;
  }
};

const TraderCard = styled.div<{ $isMyPortfolio?: boolean; $rank?: number }>`
  width: 100%;
  background: ${(props) => {
    if (props.$isMyPortfolio) {
      return 'linear-gradient(135deg, rgba(255, 204, 13, 0.15) 0%, rgba(255, 138, 0, 0.08) 100%)';
    }
    const rankStyles = props.$rank ? getMobileRankCardStyles(props.$rank) : null;
    return rankStyles ? rankStyles.background : '#0c2323';
  }};
  border: ${(props) => {
    if (props.$isMyPortfolio) {
      return '2px solid #ffcc0d';
    }
    const rankStyles = props.$rank ? getMobileRankCardStyles(props.$rank) : null;
    return rankStyles ? `1px solid ${rankStyles.border}` : '1px solid transparent';
  }};
  border-radius: 24px;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:active {
    transform: scale(0.98);
    opacity: 0.9;
  }
`;

const MyPortfolioBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 4px 10px;
  background: linear-gradient(90deg, #ffcc0d 0%, #ff8a00 100%);
  border-radius: 6px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: #0d1117;
  letter-spacing: -0.2px;
  z-index: 1;
`;

const CardHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const TraderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AvatarWrapper = styled.div`
  position: relative;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  background: #ffffff;
  border-radius: 999px;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 999px;
  object-fit: cover;
`;

const RankBadge = styled.div<{ $rank: number }>`
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 20px;
  height: 20px;
  background: #07d993;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RankNumber = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.28px;
`;

const TraderDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TraderName = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: #ffffff;
  letter-spacing: -0.36px;
`;

const CopyTradesCount = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #9496a1;
  letter-spacing: -0.28px;
`;

const CrownIcon = styled.img`
  width: 60px;
  height: 42px;
  object-fit: contain;
`;

const ROISection = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ROILeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ROILabel = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.72px;
`;

const ROIValue = styled.div<{ $isNegative?: boolean }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: ${({ $isNegative }) => ($isNegative ? '#ef4444' : '#07d962')};
  letter-spacing: -1.2px;
`;

const ChartWrapper = styled.div`
  width: 110px;
  height: 41px;
  flex-shrink: 0;
`;

const CardMetrics = styled.div`
  width: 100%;
  display: flex;
  gap: 54px;
`;

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetricLabel = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.72px;
`;

const MetricValue = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.96px;
`;

const CopyTradeButton = styled.button<{
  $isFollowing: boolean;
  $isMyPortfolio: boolean;
}>`
  width: 100%;
  height: 42px;
  padding: 0;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: #ffffff;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const PaginationContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 30px;
`;

const PaginationButton = styled.button`
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  transition: opacity 0.3s ease;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }
`;

const PaginationArrow = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  display: block;
`;

const PaginationNumber = styled.button<{ $isActive: boolean }>`
  width: 30px;
  height: 30px;
  background: ${(props) => (props.$isActive ? '#07d993' : 'transparent')};
  border: ${(props) => (props.$isActive ? 'none' : '1px solid #353535')};
  border-radius: 6px;
  color: ${(props) => (props.$isActive ? '#0d1117' : '#b3b5bd')};
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: ${(props) => (props.$isActive ? '700' : '400')};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.8;
  }
`;

// Following Tab Styles
const FollowingContentSection = styled.section`
  width: 100%;
  padding: 0 16px;
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AllTradersSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AllTradersTitle = styled.h2`
  font-family: 'Aggro', sans-serif;
  font-size: 20px;
  font-weight: 500;
  color: #ffffff;
  letter-spacing: -0.4px;
  line-height: 28px;
`;

const StatsCardMobile = styled.div`
  width: 100%;
  background: #0c2323;
  border-radius: 16px;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StatItemMobile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StatLabelMobile = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
`;

const StatValueMobile = styled.div<{ $isPositive?: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: ${(props) => (props.$isPositive ? '#07d962' : '#ffffff')};
  letter-spacing: -0.48px;
`;

const StatUnitMobile = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  letter-spacing: -0.28px;
`;

const FollowingPortfoliosSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FollowingPortfoliosTitle = styled.h2`
  font-family: 'Aggro', sans-serif;
  font-size: 20px;
  font-weight: 500;
  color: #ffffff;
  letter-spacing: -0.4px;
  line-height: 28px;
`;

const PortfoliosList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FollowingPortfolioCard = styled.div`
  background: rgba(0, 110, 73, 0.08);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FollowingCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FollowingPortfolioName = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
`;

const FollowingStatusTag = styled.span<{ $status: number }>`
  padding: 4px 10px;
  border-radius: 4px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 700;
  background: ${(props) =>
    props.$status === 1
      ? 'rgba(7, 217, 147, 0.2)'
      : props.$status === 2
        ? 'rgba(255, 193, 7, 0.2)'
        : 'rgba(255, 77, 79, 0.2)'};
  color: ${(props) =>
    props.$status === 1
      ? '#07d993'
      : props.$status === 2
        ? '#ffc107'
        : '#ff4d4f'};
`;

const FollowingCardStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FollowingStatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FollowingStatLabel = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #777986;
`;

const FollowingStatValue = styled.div<{ $isPositive?: boolean }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${(props) =>
    props.$isPositive === undefined
      ? '#ffffff'
      : props.$isPositive
        ? '#07d993'
        : '#ff4d4f'};
`;

const FollowingManageButton = styled.button`
  width: 100%;
  height: 40px;
  background: transparent;
  border: 1px solid #353535;
  border-radius: 8px;
  color: #ffffff;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 4px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #07d993;
    color: #07d993;
  }
`;

const EmptyStateContainer = styled.div`
  width: 100%;
  height: 360px;
  border: 1px solid #353535;
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const EmptyStateContent = styled.div`
  position: relative;
  width: 328px;
  height: 228px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpeechBubbleMobile = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 322px;
  height: 157.812px;
  z-index: 2;
`;

const SpeechBubbleImageMobile = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const SpeechBubbleTextMobile = styled.div`
  position: absolute;
  left: 50%;
  top: 47px;
  transform: translateX(-50%);
  font-family: 'Aggro', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #e9e9ed;
  letter-spacing: -0.32px;
  line-height: 24px;
  text-align: center;
  white-space: pre-wrap;
  z-index: 3;
  pointer-events: none;
`;

const CharacterImgMobile = styled.img`
  position: absolute;
  left: 50%;
  top: 117px;
  transform: translateX(-50%);
  width: 105px;
  height: 110px;
  object-fit: contain;
  z-index: 1;
`;

// Order History Tab Styles
const OrderHistoryContentSection = styled.section`
  width: 100%;
  padding: 0 16px;
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OrderHistoryHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OrderHistoryTitle = styled.h2`
  font-family: 'Aggro', sans-serif;
  font-size: 20px;
  font-weight: 500;
  color: #ffffff;
  letter-spacing: -0.4px;
  line-height: 28px;
`;

const TotalOrdersCardMobile = styled.div`
  width: 100%;
  background: #0c2323;
  border-radius: 16px;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TotalOrdersLabelMobile = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
`;

const TotalOrdersValueMobile = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
`;

const TotalOrdersNumberMobile = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.48px;
  text-align: center;
  width: 80px;
`;

const RefreshButtonMobile = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px 4px 6px;
  background: transparent;
  border: 1px solid #353535;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const RefreshIconMobile = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const RefreshTextMobile = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.24px;
`;

const FollowingPortfoliosSectionMobile = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FollowingPortfoliosTitleMobile = styled.h2`
  font-family: 'Aggro', sans-serif;
  font-size: 20px;
  font-weight: 500;
  color: #ffffff;
  letter-spacing: -0.4px;
  line-height: 28px;
`;

const FiltersBarMobile = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  z-index: 20;
  margin-bottom: 16px;
`;

const FiltersRowMobile = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
  position: relative;
  z-index: 10;
`;

const FilterButtonMobile = styled.button<{ $isActive?: boolean }>`
  flex: 1;
  height: 36px;
  padding: 4px 12px;
  background: rgba(91, 93, 107, 0.3);
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(91, 93, 107, 0.4);
  }
`;

const FilterTextMobile = styled.span<{ $isActive?: boolean }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => (props.$isActive ? '#ffffff' : '#b3b5bd')};
  letter-spacing: -0.28px;
`;

const FilterIconMobile = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const ReloadButtonMobile = styled.button`
  width: 36px;
  height: 36px;
  padding: 0;
  background: rgba(91, 93, 107, 0.3);
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(91, 93, 107, 0.4);
  }
`;

const ReloadIconMobile = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const OrdersTableContainerMobile = styled.div`
  width: 100%;
  background: #0c2323;
  border-radius: 16px;
  min-height: 360px;
  padding: 16px;
`;

const OrdersListMobile = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OrderItemMobile = styled.div`
  width: 100%;
  padding: 16px;
  background: rgba(0, 110, 73, 0.08);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OrderItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const OrderItemLabel = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.24px;
`;

const OrderItemValue = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  letter-spacing: -0.28px;
`;

const EmptyTextMobile = styled.div`
  width: 100%;
  padding: 40px 0;
  text-align: center;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #ffffff;
`;
